import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getLeaderboardRankings, getUserRanking } from '../services/leaderboard';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import toast from 'react-hot-toast';

const LeaderboardPage = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [rankings, setRankings] = useState([]);
  const [userRanking, setUserRanking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    sortBy: 'totalVotes',
    minIdeas: 0,
    timePeriod: 'all',
    limit: 50
  });

  useEffect(() => {
    fetchLeaderboard();
  }, [filters, currentUser]);

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      const data = await getLeaderboardRankings(filters);
      setRankings(data);

      // Fetch current user's ranking if logged in
      if (currentUser) {
        const userRank = await getUserRanking(currentUser.uid, filters);
        setUserRanking(userRank);
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      toast.error('Failed to load leaderboard. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const resetFilters = () => {
    setFilters({
      sortBy: 'totalVotes',
      minIdeas: 0,
      timePeriod: 'all',
      limit: 50
    });
  };

  const getRankBadgeStyle = (rank) => {
    if (rank === 1) {
      return 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-white shadow-lg';
    } else if (rank === 2) {
      return 'bg-gradient-to-br from-gray-300 to-gray-500 text-white shadow-lg';
    } else if (rank === 3) {
      return 'bg-gradient-to-br from-orange-400 to-orange-600 text-white shadow-lg';
    } else {
      return 'bg-gradient-to-br from-primary-400 to-primary-600 text-white';
    }
  };

  const formatROI = (roi) => {
    if (!roi && roi !== 0) return 'N/A';
    const formatted = roi.toFixed(1);
    return roi > 0 ? `+${formatted}%` : `${formatted}%`;
  };

  const getROIColor = (roi) => {
    if (!roi && roi !== 0) return 'text-gray-500';
    return roi > 0 ? 'text-green-600 font-semibold' : roi < 0 ? 'text-red-600 font-semibold' : 'text-gray-600';
  };

  const getSortLabel = (sortBy) => {
    switch (sortBy) {
      case 'totalVotes':
        return 'Total Votes';
      case 'averageROI':
        return 'Average ROI';
      case 'totalIdeas':
        return 'Total Ideas';
      case 'totalViews':
        return 'Total Views';
      case 'totalROI':
        return 'Cumulative ROI';
      default:
        return 'Total Votes';
    }
  };

  const getStatusLabel = (user) => {
    if (user.activeIdeas > 0) {
      return { label: 'Active', color: 'bg-green-100 text-green-700' };
    } else if (user.totalIdeas > 0) {
      return { label: 'Inactive', color: 'bg-gray-100 text-gray-600' };
    } else {
      return { label: 'New', color: 'bg-blue-100 text-blue-700' };
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white">
      <Navbar />

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold mb-4">Leaderboard</h1>
          <p className="text-xl text-primary-100">
            Top contributors ranked by performance and community engagement
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* User's Current Ranking (if logged in) */}
        {currentUser && userRanking && (
          <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl shadow-lg p-6 mb-8 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold mb-2">Your Current Ranking</h3>
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <p className="text-sm opacity-90">Rank</p>
                    <p className="text-3xl font-bold">#{userRanking.rank}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm opacity-90">Ideas</p>
                    <p className="text-3xl font-bold">{userRanking.totalIdeas}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm opacity-90">Total Votes</p>
                    <p className="text-3xl font-bold">{userRanking.totalVotes}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm opacity-90">Avg ROI</p>
                    <p className="text-3xl font-bold">
                      {userRanking.resolvedIdeas > 0 ? formatROI(userRanking.averageROI) : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-6 py-3 bg-white text-primary-600 rounded-lg hover:bg-primary-50 transition-colors font-medium"
              >
                View Dashboard
              </button>
            </div>
          </div>
        )}

        {/* Filters Section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8 border border-gray-100">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Filters & Sorting</h2>
            <button
              onClick={resetFilters}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Reset All
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Sort By */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sort By
              </label>
              <select
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="totalVotes">Most Votes</option>
                <option value="averageROI">Best Avg ROI</option>
                <option value="totalROI">Best Cumulative ROI</option>
                <option value="totalIdeas">Most Ideas</option>
                <option value="totalViews">Most Views</option>
              </select>
            </div>

            {/* Time Period */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Time Period
              </label>
              <select
                value={filters.timePeriod}
                onChange={(e) => handleFilterChange('timePeriod', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Time</option>
                <option value="year">This Year</option>
                <option value="quarter">This Quarter</option>
                <option value="month">This Month</option>
              </select>
            </div>

            {/* Minimum Ideas */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minimum Ideas
              </label>
              <select
                value={filters.minIdeas}
                onChange={(e) => handleFilterChange('minIdeas', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="0">Any</option>
                <option value="1">1+</option>
                <option value="5">5+</option>
                <option value="10">10+</option>
                <option value="20">20+</option>
              </select>
            </div>

            {/* Results Limit */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Show Top
              </label>
              <select
                value={filters.limit}
                onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
          </div>

          <div className="mt-4 text-sm text-gray-600">
            <p>
              <strong>Note:</strong> Rankings based on {getSortLabel(filters.sortBy).toLowerCase()}.
              {filters.sortBy === 'averageROI' && ' Ties broken by number of resolved ideas.'}
            </p>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        )}

        {/* Empty State */}
        {!loading && rankings.length === 0 && (
          <div className="text-center py-20 bg-white rounded-lg shadow-sm">
            <svg
              className="mx-auto h-24 w-24 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
            <h3 className="mt-6 text-xl font-medium text-gray-900">No Rankings Yet</h3>
            <p className="mt-2 text-gray-500">
              Be the first to submit an idea and get on the leaderboard!
            </p>
            {currentUser && (
              <button
                onClick={() => navigate('/submit-idea')}
                className="mt-6 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
              >
                Submit Your First Idea
              </button>
            )}
          </div>
        )}

        {/* Leaderboard Table */}
        {!loading && rankings.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Rank
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Member
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Ideas
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Total Votes
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Avg ROI
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Total ROI
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Best Idea
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {rankings.map((user) => {
                    const status = getStatusLabel(user);
                    const isCurrentUser = currentUser && user.userId === currentUser.uid;
                    
                    return (
                      <tr
                        key={user.userId}
                        className={`hover:bg-gray-50 transition-colors ${
                          isCurrentUser ? 'bg-primary-50' : ''
                        }`}
                      >
                        {/* Rank */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div
                              className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${getRankBadgeStyle(
                                user.rank
                              )}`}
                            >
                              {user.rank}
                            </div>
                          </div>
                        </td>

                        {/* Member */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div 
                              className="flex-shrink-0 h-10 w-10 cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/users/${user.userId}`);
                              }}
                            >
                              <div className="h-10 w-10 rounded-full bg-primary-600 flex items-center justify-center text-white font-semibold">
                                {user.displayName?.charAt(0).toUpperCase() || '?'}
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/users/${user.userId}`);
                                  }}
                                  className="text-primary-600 hover:text-primary-700 hover:underline transition-colors"
                                >
                                  {user.displayName}
                                </button>
                                {isCurrentUser && (
                                  <span className="ml-2 px-2 py-1 text-xs bg-primary-100 text-primary-700 rounded-full">
                                    You
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Ideas */}
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="text-sm text-gray-900">{user.totalIdeas}</div>
                          <div className="text-xs text-gray-500">
                            {user.resolvedIdeas} resolved
                          </div>
                        </td>

                        {/* Total Votes */}
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="text-sm font-semibold text-gray-900">
                            {user.totalVotes}
                          </div>
                        </td>

                        {/* Avg ROI */}
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className={`text-sm font-semibold ${getROIColor(user.averageROI)}`}>
                            {user.resolvedIdeas > 0 ? formatROI(user.averageROI) : 'N/A'}
                          </div>
                        </td>

                        {/* Total ROI */}
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className={`text-sm font-semibold ${getROIColor(user.totalROI)}`}>
                            {user.resolvedIdeas > 0 ? formatROI(user.totalROI) : 'N/A'}
                          </div>
                        </td>

                        {/* Best Idea */}
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          {user.bestIdea ? (
                            <div>
                              <div className={`text-sm font-semibold ${getROIColor(user.bestIdea.roi)}`}>
                                {formatROI(user.bestIdea.roi)}
                              </div>
                              <button
                                onClick={() => navigate(`/ideas/${user.bestIdea.id}`)}
                                className="text-xs text-primary-600 hover:text-primary-700 hover:underline"
                              >
                                View
                              </button>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">N/A</span>
                          )}
                        </td>

                        {/* Status */}
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span
                            className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${status.color}`}
                          >
                            {status.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Info Section */}
        {!loading && rankings.length > 0 && (
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-3">
              How Rankings Work
            </h3>
            <ul className="space-y-2 text-sm text-blue-800">
              <li className="flex items-start">
                <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span><strong>Total Votes:</strong> Sum of all votes received across all your ideas</span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span><strong>Average ROI:</strong> Average return on investment across all resolved ideas</span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span><strong>Total ROI:</strong> Cumulative return across all resolved ideas</span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span><strong>Status:</strong> Active members have at least one active idea currently open</span>
              </li>
            </ul>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default LeaderboardPage;

